// @noflow
// Config
import { format as formatDate } from 'date-fns'
import { cs, de, enGB, fr, it, nl, nlBE, pl } from 'date-fns/locale'
import i18n from 'i18next'
import Pseudo from 'i18next-pseudo'
import Cookies from 'js-cookie'
import { initReactI18next } from 'react-i18next'

import * as StringHelper from '@/utils/StringHelper'
import { ordinality } from '@/utils/ordinal'

import { ensureNever } from '@/typescript/utils'

import * as CSCZData from '../../../config/locales/cs_CZ.yml'
import * as DEDEData from '../../../config/locales/de_DE.yml'
import * as ENData from '../../../config/locales/en.yml'
import * as FRData from '../../../config/locales/fr.yml'
import * as ITITData from '../../../config/locales/it_IT.yml'
import * as NLData from '../../../config/locales/nl.yml'
import * as NLBeData from '../../../config/locales/nl_BE.yml'
import * as PLData from '../../../config/locales/pl.yml'
import * as PLPLData from '../../../config/locales/pl_PL.yml'

// Due to needing to use snake_case elsewhere and i18next expecting kebab-case,
// we have the two different type declarations here to satisfy i18next's
// requirement whilst maintaining a consistent use of snake_case everywhere
// else possible

type Language =
  | 'nl'
  | 'en'
  | 'nl_BE'
  | 'fr'
  | 'pl'
  | 'pl_PL'
  | 'de_DE'
  | 'cs_CZ'
  | 'it_IT'

type SupportedLanguage =
  | 'en'
  | 'nl'
  | 'nl-BE'
  | 'fr'
  | 'pl'
  | 'pl-PL'
  | 'de-DE'
  | 'cs-CZ'
  | 'it-IT'

const supportedLanguages: Array<SupportedLanguage> = [
  'nl',
  'en',
  'nl-BE',
  'fr',
  'pl',
  'pl-PL',
  'de-DE',
  'cs-CZ',
  'it-IT'
]

const resources = {
  en: {
    app: ENData.default.en.app,
    dynamic: ENData.default.en.dynamic,
    surveys: ENData.default.en.surveys,
    learn_more_about_butternut:
      ENData.default.en.shared.learn_more_about_butternut,
    frequently_asked_questions:
      ENData.default.en.shared.frequently_asked_questions,
    reviews: ENData.default.en.reviews,
    account: ENData.default.en.account,
    homepage: ENData.default.en.homepage,
    home_page: ENData.default.en.home_page,
    contacts: ENData.default.en.contacts,
    our_products: ENData.default.en.our_products,
    our_story: ENData.default.en.our_story,
    making_our_meals: ENData.default.en.making_our_meals,
    wizard_flow: ENData.default.en.wizard_flow,
    self_resolution_flow: ENData.default.en.self_resolution_flow,
    customer_issue_management_flow:
      ENData.default.en.customer_issue_management_flow,
    plans_flow: ENData.default.en.plans_flow,
    simplified_plans_flow: ENData.default.en.simplified_plans_flow,
    ds_app_pay_on_signup_flow: ENData.default.en.ds_app_pay_on_signup_flow,
    checkout: ENData.default.en.checkout,
    thank_you: ENData.default.en.thank_you,
    models: ENData.default.en.models,
    product_categories: ENData.default.en.models.product_categories,
    post_signup_wizard: ENData.default.en.post_signup_wizard,
    dashboard: ENData.default.en.dashboard,
    shipping_countries: ENData.default.en.models.shipping_countries,
    calories_covered: ENData.default.en.shared.calories_covered,
    text_field: ENData.default.en.shared.text_field,
    footer: ENData.default.en.shared.footer,
    navigation: ENData.default.en.shared.navigation,
    unavailable_dates_card: ENData.default.en.shared.unavailable_dates_card,
    expert: ENData.default.en.shared.expert,
    paid_traffic_breeds: ENData.default.en.paid_traffic.breed_autocomplete,
    paid_traffic: ENData.default.en.paid_traffic,
    one_time_purchase_footer: ENData.default.en.shared.one_time_purchase_footer,
    order_summary: ENData.default.en.shared.order_summary,
    shared: ENData.default.en.shared,
    languages: ENData.default.en.models.languages,
    localisation_modal: ENData.default.en.shared.localisation_modal,
    plan_management: ENData.default.en.plan_management,
    onboarding_quiz: ENData.default.en.onboarding_quiz,
    product_modal: ENData.default.en.shared.product_modal,
    gql_errors: ENData.default.en.gql_errors,
    archive_reasons: ENData.default.en.archive_reasons,
    care: ENData.default.en.care,
    orders: ENData.default.en.orders,
    order: ENData.default.en.order,
    butternut_app: ENData.default.en.butternut_app,
    atoms: ENData.default.en.atoms,
    molecules: ENData.default.en.molecules,
    organisms: ENData.default.en.organisms,
    one_off_box_page: ENData.default.en.one_off_box_page,
    swap_and_save: ENData.default.en.swap_and_save,
    limited_offer_paused_page: ENData.default.en.limited_offer_paused_page,
    treatments_paused_page: ENData.default.en.treatments_paused_page,
    ribbon: ENData.default.en.ribbon,
    blog: ENData.default.en.blog,
    ambassadors: ENData.default.en.ambassadors,
    ambassadors_shared: ENData.default.en.ambassadors_shared,
    ambassadors_funnel: ENData.default.en.ambassadors_funnel,
    ambassadors_account: ENData.default.en.ambassadors_account,
    modals: ENData.default.en.modals,
    routes: ENData.default.en.routes,
    localised_address: ENData.default.en.localised_address
  },
  nl: {
    dynamic: NLData.default.nl.dynamic,
    surveys: NLData.default.nl.surveys,
    learn_more_about_butternut:
      NLData.default.nl.shared.learn_more_about_butternut,
    frequently_asked_questions:
      NLData.default.nl.shared.frequently_asked_questions,
    reviews: NLData.default.nl.reviews,
    account: NLData.default.nl.account,
    homepage: NLData.default.nl.homepage,
    home_page: NLData.default.nl.home_page,
    contacts: NLData.default.nl.contacts,
    our_products: NLData.default.nl.our_products,
    our_story: NLData.default.nl.our_story,
    making_our_meals: NLData.default.nl.making_our_meals,
    wizard_flow: NLData.default.nl.wizard_flow,
    self_resolution_flow: NLData.default.nl.self_resolution_flow,
    customer_issue_management_flow:
      NLData.default.nl.customer_issue_management_flow,
    plans_flow: NLData.default.nl.plans_flow,
    simplified_plans_flow: NLData.default.nl.simplified_plans_flow,
    checkout: NLData.default.nl.checkout,
    thank_you: NLData.default.nl.thank_you,
    models: NLData.default.nl.models,
    product_categories: NLData.default.nl.models.product_categories,
    post_signup_wizard: NLData.default.nl.post_signup_wizard,
    dashboard: NLData.default.nl.dashboard,
    ds_app_pay_on_signup_flow: NLData.default.nl.ds_app_pay_on_signup_flow,
    shipping_countries: NLData.default.nl.models.shipping_countries,
    calories_covered: NLData.default.nl.shared.calories_covered,
    text_field: NLData.default.nl.shared.text_field,
    footer: NLData.default.nl.shared.footer,
    navigation: NLData.default.nl.shared.navigation,
    unavailable_dates_card: NLData.default.nl.shared.unavailable_dates_card,
    expert: NLData.default.nl.shared.expert,
    paid_traffic_breeds: NLData.default.nl.paid_traffic.breed_autocomplete,
    paid_traffic: NLData.default.nl.paid_traffic,
    one_time_purchase_footer: NLData.default.nl.shared.one_time_purchase_footer,
    order_summary: NLData.default.nl.shared.order_summary,
    shared: NLData.default.nl.shared,
    languages: NLData.default.nl.models.languages,
    localisation_modal: NLData.default.nl.shared.localisation_modal,
    plan_management: NLData.default.nl.plan_management,
    onboarding_quiz: NLData.default.nl.onboarding_quiz,
    product_modal: NLData.default.nl.shared.product_modal,
    gql_errors: NLData.default.nl.gql_errors,
    archive_reasons: NLData.default.nl.archive_reasons,
    care: NLData.default.nl.care,
    orders: NLData.default.nl.orders,
    order: NLData.default.nl.order,
    butternut_app: NLData.default.nl.butternut_app,
    atoms: NLData.default.nl.atoms,
    molecules: NLData.default.nl.molecules,
    organisms: NLData.default.nl.organisms,
    one_off_box_page: NLData.default.nl.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: NLData.default.nl.swap_and_save,
    limited_offer_paused_page: NLData.default.nl.limited_offer_paused_page,
    treatments_paused_page: NLData.default.nl.treatments_paused_page,
    ribbon: NLData.default.nl.ribbon,
    ambassadors: NLData.default.nl.ambassadors,
    ambassadors_shared: NLData.default.nl.ambassadors_shared,
    ambassadors_funnel: NLData.default.nl.ambassadors_funnel,
    ambassadors_account: NLData.default.nl.ambassadors_account,
    modals: NLData.default.nl.modals,
    blog: NLData.default.nl.blog,
    routes: NLData.default.nl.routes,
    localised_address: NLData.default.nl.localised_address
  },
  'nl-BE': {
    frequently_asked_questions:
      NLBeData.default.nl_BE.shared.frequently_asked_questions,
    account: NLBeData.default.nl_BE.account,
    homepage: NLBeData.default.nl_BE.homepage,
    home_page: NLBeData.default.nl_BE.home_page,
    contacts: NLBeData.default.nl_BE.contacts,
    ds_app_pay_on_signup_flow: NLBeData.default.nl_BE.ds_app_pay_on_signup_flow,
    our_story: NLBeData.default.nl_BE.our_story,
    wizard_flow: NLBeData.default.nl_BE.wizard_flow,
    plans_flow: NLBeData.default.nl_BE.plans_flow,
    checkout: NLBeData.default.nl_BE.checkout,
    models: NLBeData.default.nl_BE.models,
    post_signup_wizard: NLBeData.default.nl_BE.post_signup_wizard,
    text_field: NLBeData.default.nl_BE.shared.text_field,
    footer: NLBeData.default.nl_BE.shared.footer,
    paid_traffic: NLBeData.default.nl_BE.paid_traffic,
    plan_management: NLBeData.default.nl_BE.plan_management,
    order: NLBeData.default.nl_BE.order,
    molecules: NLBeData.default.nl_BE.molecules,
    organisms: NLBeData.default.nl_BE.organisms,
    modals: NLBeData.default.nl_BE.modals,
    limited_offer_paused_page: NLBeData.default.nl_BE.limited_offer_paused_page,
    ambassadors: NLBeData.default.nl_BE.ambassadors,
    ambassadors_shared: NLBeData.default.nl_BE.ambassadors_shared,
    ambassadors_funnel: NLBeData.default.nl_BE.ambassadors_funnel,
    ambassadors_account: NLBeData.default.nl_BE.ambassadors_account,
    blog: NLBeData.default.nl_BE.blog,
    routes: NLBeData.default.nl_BE.routes,
    localised_address: NLBeData.default.nl_BE.localised_address
  },
  pl: {
    dynamic: PLData.default.pl.dynamic,
    surveys: PLData.default.pl.surveys,
    learn_more_about_butternut:
      PLData.default.pl.shared.learn_more_about_butternut,
    frequently_asked_questions:
      PLData.default.pl.shared.frequently_asked_questions,
    reviews: PLData.default.pl.reviews,
    account: PLData.default.pl.account,
    homepage: PLData.default.pl.homepage,
    home_page: PLData.default.pl.home_page,
    contacts: PLData.default.pl.contacts,
    our_products: PLData.default.pl.our_products,
    our_story: PLData.default.pl.our_story,
    making_our_meals: PLData.default.pl.making_our_meals,
    wizard_flow: PLData.default.pl.wizard_flow,
    self_resolution_flow: PLData.default.pl.self_resolution_flow,
    customer_issue_management_flow:
      PLData.default.pl.customer_issue_management_flow,
    plans_flow: PLData.default.pl.plans_flow,
    simplified_plans_flow: PLData.default.pl.simplified_plans_flow,
    checkout: PLData.default.pl.checkout,
    thank_you: PLData.default.pl.thank_you,
    models: PLData.default.pl.models,
    product_categories: PLData.default.pl.models.product_categories,
    post_signup_wizard: PLData.default.pl.post_signup_wizard,
    dashboard: PLData.default.pl.dashboard,
    ds_app_pay_on_signup_flow: PLData.default.pl.ds_app_pay_on_signup_flow,
    shipping_countries: PLData.default.pl.models.shipping_countries,
    calories_covered: PLData.default.pl.shared.calories_covered,
    text_field: PLData.default.pl.shared.text_field,
    footer: PLData.default.pl.shared.footer,
    navigation: PLData.default.pl.shared.navigation,
    unavailable_dates_card: PLData.default.pl.shared.unavailable_dates_card,
    expert: PLData.default.pl.shared.expert,
    paid_traffic_breeds: PLData.default.pl.paid_traffic.breed_autocomplete,
    paid_traffic: PLData.default.pl.paid_traffic,
    one_time_purchase_footer: PLData.default.pl.shared.one_time_purchase_footer,
    order_summary: PLData.default.pl.shared.order_summary,
    shared: PLData.default.pl.shared,
    languages: PLData.default.pl.models.languages,
    localisation_modal: PLData.default.pl.shared.localisation_modal,
    plan_management: PLData.default.pl.plan_management,
    onboarding_quiz: PLData.default.pl.onboarding_quiz,
    product_modal: PLData.default.pl.shared.product_modal,
    gql_errors: PLData.default.pl.gql_errors,
    archive_reasons: PLData.default.pl.archive_reasons,
    care: PLData.default.pl.care,
    orders: PLData.default.pl.orders,
    order: PLData.default.pl.order,
    butternut_app: PLData.default.pl.butternut_app,
    atoms: PLData.default.pl.atoms,
    molecules: PLData.default.pl.molecules,
    organisms: PLData.default.pl.organisms,
    one_off_box_page: PLData.default.pl.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: PLData.default.pl.swap_and_save,
    limited_offer_paused_page: PLData.default.pl.limited_offer_paused_page,
    treatments_paused_page: PLData.default.pl.treatments_paused_page,
    ribbon: PLData.default.pl.ribbon,
    invoices: PLData.default.pl.invoices,
    ambassadors: PLData.default.pl.ambassadors,
    ambassadors_shared: PLData.default.pl.ambassadors_shared,
    ambassadors_funnel: PLData.default.pl.ambassadors_funnel,
    ambassadors_account: PLData.default.pl.ambassadors_account,
    modals: PLData.default.pl.modals,
    blog: PLData.default.pl.blog,
    routes: PLData.default.pl.routes,
    localised_address: PLData.default.pl.localised_address
  },
  'pl-PL': {
    learn_more_about_butternut:
      PLPLData.default.pl_PL.shared.learn_more_about_butternut,
    frequently_asked_questions:
      PLPLData.default.pl_PL.shared.frequently_asked_questions,
    reviews: PLPLData.default.pl_PL.reviews,
    account: PLPLData.default.pl_PL.account,
    homepage: PLPLData.default.pl_PL.homepage,
    home_page: PLPLData.default.pl_PL.home_page,
    contacts: PLPLData.default.pl_PL.contacts,
    our_products: PLPLData.default.pl_PL.our_products,
    our_story: PLPLData.default.pl_PL.our_story,
    making_our_meals: PLPLData.default.pl_PL.making_our_meals,
    wizard_flow: PLPLData.default.pl_PL.wizard_flow,
    self_resolution_flow: PLPLData.default.pl_PL.self_resolution_flow,
    customer_issue_management_flow:
      PLPLData.default.pl_PL.customer_issue_management_flow,
    plans_flow: PLPLData.default.pl_PL.plans_flow,
    simplified_plans_flow: PLPLData.default.pl_PL.simplified_plans_flow,
    checkout: PLPLData.default.pl_PL.checkout,
    thank_you: PLPLData.default.pl_PL.thank_you,
    models: PLPLData.default.pl_PL.models,
    post_signup_wizard: PLPLData.default.pl_PL.post_signup_wizard,
    dashboard: PLPLData.default.pl_PL.dashboard,
    calories_covered: PLPLData.default.pl_PL.shared.calories_covered,
    text_field: PLPLData.default.pl_PL.shared.text_field,
    footer: PLPLData.default.pl_PL.shared.footer,
    navigation: PLPLData.default.pl_PL.shared.navigation,
    unavailable_dates_card:
      PLPLData.default.pl_PL.shared.unavailable_dates_card,
    expert: PLPLData.default.pl_PL.shared.expert,
    paid_traffic_breeds: PLPLData.default.pl_PL.paid_traffic.breed_autocomplete,
    paid_traffic: PLPLData.default.pl_PL.paid_traffic,
    one_time_purchase_footer:
      PLPLData.default.pl_PL.shared.one_time_purchase_footer,
    order_summary: PLPLData.default.pl_PL.shared.order_summary,
    shared: PLPLData.default.pl_PL.shared,
    localisation_modal: PLPLData.default.pl_PL.shared.localisation_modal,
    plan_management: PLPLData.default.pl_PL.plan_management,
    onboarding_quiz: PLPLData.default.pl_PL.onboarding_quiz,
    product_modal: PLPLData.default.pl_PL.shared.product_modal,
    gql_errors: PLPLData.default.pl_PL.gql_errors,
    archive_reasons: PLPLData.default.pl_PL.archive_reasons,
    care: PLPLData.default.pl_PL.care,
    orders: PLPLData.default.pl_PL.orders,
    order: PLPLData.default.pl_PL.order,
    butternut_app: PLPLData.default.pl_PL.butternut_app,
    atoms: PLPLData.default.pl_PL.atoms,
    molecules: PLPLData.default.pl_PL.molecules,
    organisms: PLPLData.default.pl_PL.organisms,
    one_off_box_page: PLPLData.default.pl_PL.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: PLPLData.default.pl_PL.swap_and_save,
    limited_offer_paused_page: PLPLData.default.pl_PL.limited_offer_paused_page,
    treatments_paused_page: PLPLData.default.pl_PL.treatments_paused_page,
    invoices: PLPLData.default.pl_PL.invoices,
    ribbon: PLPLData.default.pl_PL.ribbon,
    ambassadors: PLPLData.default.pl_PL.ambassadors,
    ambassadors_shared: PLPLData.default.pl_PL.ambassadors_shared,
    ambassadors_funnel: PLPLData.default.pl_PL.ambassadors_funnel,
    ambassadors_account: PLPLData.default.pl_PL.ambassadors_account,
    modals: PLPLData.default.pl_PL.modals,
    blog: PLPLData.default.pl_PL.blog,
    routes: PLPLData.default.pl_PL.routes,
    localised_address: PLPLData.default.pl_PL.localised_address
  },
  fr: {
    dynamic: FRData.default.fr.dynamic,
    surveys: FRData.default.fr.surveys,
    learn_more_about_butternut:
      FRData.default.fr.shared.learn_more_about_butternut,
    frequently_asked_questions:
      FRData.default.fr.shared.frequently_asked_questions,
    reviews: FRData.default.fr.reviews,
    account: FRData.default.fr.account,
    homepage: FRData.default.fr.homepage,
    home_page: FRData.default.fr.home_page,
    contacts: FRData.default.fr.contacts,
    our_products: FRData.default.fr.our_products,
    our_story: FRData.default.fr.our_story,
    making_our_meals: FRData.default.fr.making_our_meals,
    wizard_flow: FRData.default.fr.wizard_flow,
    self_resolution_flow: FRData.default.fr.self_resolution_flow,
    customer_issue_management_flow:
      FRData.default.fr.customer_issue_management_flow,
    plans_flow: FRData.default.fr.plans_flow,
    simplified_plans_flow: FRData.default.fr.simplified_plans_flow,
    checkout: FRData.default.fr.checkout,
    thank_you: FRData.default.fr.thank_you,
    models: FRData.default.fr.models,
    product_categories: FRData.default.fr.models.product_categories,
    post_signup_wizard: FRData.default.fr.post_signup_wizard,
    dashboard: FRData.default.fr.dashboard,
    ds_app_pay_on_signup_flow: FRData.default.fr.ds_app_pay_on_signup_flow,
    shipping_countries: FRData.default.fr.models.shipping_countries,
    calories_covered: FRData.default.fr.shared.calories_covered,
    text_field: FRData.default.fr.shared.text_field,
    footer: FRData.default.fr.shared.footer,
    navigation: FRData.default.fr.shared.navigation,
    unavailable_dates_card: FRData.default.fr.shared.unavailable_dates_card,
    expert: FRData.default.fr.shared.expert,
    paid_traffic_breeds: FRData.default.fr.paid_traffic.breed_autocomplete,
    paid_traffic: FRData.default.fr.paid_traffic,
    one_time_purchase_footer: FRData.default.fr.shared.one_time_purchase_footer,
    order_summary: FRData.default.fr.shared.order_summary,
    shared: FRData.default.fr.shared,
    languages: FRData.default.fr.models.languages,
    localisation_modal: FRData.default.fr.shared.localisation_modal,
    plan_management: FRData.default.fr.plan_management,
    onboarding_quiz: FRData.default.fr.onboarding_quiz,
    product_modal: FRData.default.fr.shared.product_modal,
    gql_errors: FRData.default.fr.gql_errors,
    archive_reasons: FRData.default.fr.archive_reasons,
    care: FRData.default.fr.care,
    orders: FRData.default.fr.orders,
    order: FRData.default.fr.order,
    butternut_app: FRData.default.fr.butternut_app,
    atoms: FRData.default.fr.atoms,
    molecules: FRData.default.fr.molecules,
    organisms: FRData.default.fr.organisms,
    one_off_box_page: FRData.default.fr.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: FRData.default.fr.swap_and_save,
    limited_offer_paused_page: FRData.default.fr.limited_offer_paused_page,
    treatments_paused_page: FRData.default.fr.treatments_paused_page,
    ribbon: FRData.default.fr.ribbon,
    invoices: FRData.default.fr.invoices,
    ambassadors: FRData.default.fr.ambassadors,
    ambassadors_shared: FRData.default.fr.ambassadors_shared,
    ambassadors_funnel: FRData.default.fr.ambassadors_funnel,
    ambassadors_account: FRData.default.fr.ambassadors_account,
    modals: FRData.default.fr.modals,
    blog: FRData.default.fr.blog,
    routes: FRData.default.fr.routes,
    localised_address: FRData.default.fr.localised_address
  },
  'cs-CZ': {
    dynamic: CSCZData.default.cs_CZ.dynamic,
    surveys: CSCZData.default.cs_CZ.surveys,
    learn_more_about_butternut:
      CSCZData.default.cs_CZ.shared.learn_more_about_butternut,
    frequently_asked_questions:
      CSCZData.default.cs_CZ.shared.frequently_asked_questions,
    reviews: CSCZData.default.cs_CZ.reviews,
    account: CSCZData.default.cs_CZ.account,
    homepage: CSCZData.default.cs_CZ.homepage,
    home_page: CSCZData.default.cs_CZ.home_page,
    contacts: CSCZData.default.cs_CZ.contacts,
    our_products: CSCZData.default.cs_CZ.our_products,
    our_story: CSCZData.default.cs_CZ.our_story,
    making_our_meals: CSCZData.default.cs_CZ.making_our_meals,
    wizard_flow: CSCZData.default.cs_CZ.wizard_flow,
    self_resolution_flow: CSCZData.default.cs_CZ.self_resolution_flow,
    customer_issue_management_flow:
      CSCZData.default.cs_CZ.customer_issue_management_flow,
    plans_flow: CSCZData.default.cs_CZ.plans_flow,
    simplified_plans_flow: CSCZData.default.cs_CZ.simplified_plans_flow,
    checkout: CSCZData.default.cs_CZ.checkout,
    thank_you: CSCZData.default.cs_CZ.thank_you,
    models: CSCZData.default.cs_CZ.models,
    product_categories: CSCZData.default.cs_CZ.models.product_categories,
    post_signup_wizard: CSCZData.default.cs_CZ.post_signup_wizard,
    dashboard: CSCZData.default.cs_CZ.dashboard,
    ds_app_pay_on_signup_flow: CSCZData.default.cs_CZ.ds_app_pay_on_signup_flow,
    shipping_countries: CSCZData.default.cs_CZ.models.shipping_countries,
    calories_covered: CSCZData.default.cs_CZ.shared.calories_covered,
    text_field: CSCZData.default.cs_CZ.shared.text_field,
    footer: CSCZData.default.cs_CZ.shared.footer,
    navigation: CSCZData.default.cs_CZ.shared.navigation,
    unavailable_dates_card:
      CSCZData.default.cs_CZ.shared.unavailable_dates_card,
    expert: CSCZData.default.cs_CZ.shared.expert,
    paid_traffic_breeds: CSCZData.default.cs_CZ.paid_traffic.breed_autocomplete,
    paid_traffic: CSCZData.default.cs_CZ.paid_traffic,
    one_time_purchase_footer:
      CSCZData.default.cs_CZ.shared.one_time_purchase_footer,
    order_summary: CSCZData.default.cs_CZ.shared.order_summary,
    shared: CSCZData.default.cs_CZ.shared,
    languages: CSCZData.default.cs_CZ.models.languages,
    localisation_modal: CSCZData.default.cs_CZ.shared.localisation_modal,
    plan_management: CSCZData.default.cs_CZ.plan_management,
    onboarding_quiz: CSCZData.default.cs_CZ.onboarding_quiz,
    product_modal: CSCZData.default.cs_CZ.shared.product_modal,
    gql_errors: CSCZData.default.cs_CZ.gql_errors,
    archive_reasons: CSCZData.default.cs_CZ.archive_reasons,
    care: CSCZData.default.cs_CZ.care,
    orders: CSCZData.default.cs_CZ.orders,
    order: CSCZData.default.cs_CZ.order,
    butternut_app: CSCZData.default.cs_CZ.butternut_app,
    atoms: CSCZData.default.cs_CZ.atoms,
    molecules: CSCZData.default.cs_CZ.molecules,
    organisms: CSCZData.default.cs_CZ.organisms,
    one_off_box_page: CSCZData.default.cs_CZ.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: CSCZData.default.cs_CZ.swap_and_save,
    limited_offer_paused_page: CSCZData.default.cs_CZ.limited_offer_paused_page,
    treatments_paused_page: CSCZData.default.cs_CZ.treatments_paused_page,
    ribbon: CSCZData.default.cs_CZ.ribbon,
    invoices: CSCZData.default.cs_CZ.invoices,
    ambassadors: CSCZData.default.cs_CZ.ambassadors,
    ambassadors_shared: CSCZData.default.cs_CZ.ambassadors_shared,
    ambassadors_funnel: CSCZData.default.cs_CZ.ambassadors_funnel,
    ambassadors_account: CSCZData.default.cs_CZ.ambassadors_account,
    modals: CSCZData.default.cs_CZ.modals,
    blog: CSCZData.default.cs_CZ.blog,
    routes: CSCZData.default.cs_CZ.routes,
    localised_address: CSCZData.default.cs_CZ.localised_address
  },
  'de-DE': {
    dynamic: DEDEData.default.de_DE.dynamic,
    surveys: DEDEData.default.de_DE.surveys,
    learn_more_about_butternut:
      DEDEData.default.de_DE.shared.learn_more_about_butternut,
    frequently_asked_questions:
      DEDEData.default.de_DE.shared.frequently_asked_questions,
    reviews: DEDEData.default.de_DE.reviews,
    account: DEDEData.default.de_DE.account,
    homepage: DEDEData.default.de_DE.homepage,
    home_page: DEDEData.default.de_DE.home_page,
    contacts: DEDEData.default.de_DE.contacts,
    our_products: DEDEData.default.de_DE.our_products,
    our_story: DEDEData.default.de_DE.our_story,
    making_our_meals: DEDEData.default.de_DE.making_our_meals,
    wizard_flow: DEDEData.default.de_DE.wizard_flow,
    self_resolution_flow: DEDEData.default.de_DE.self_resolution_flow,
    customer_issue_management_flow:
      DEDEData.default.de_DE.customer_issue_management_flow,
    plans_flow: DEDEData.default.de_DE.plans_flow,
    simplified_plans_flow: DEDEData.default.de_DE.simplified_plans_flow,
    checkout: DEDEData.default.de_DE.checkout,
    thank_you: DEDEData.default.de_DE.thank_you,
    models: DEDEData.default.de_DE.models,
    product_categories: DEDEData.default.de_DE.models.product_categories,
    post_signup_wizard: DEDEData.default.de_DE.post_signup_wizard,
    dashboard: DEDEData.default.de_DE.dashboard,
    ds_app_pay_on_signup_flow: DEDEData.default.de_DE.ds_app_pay_on_signup_flow,
    shipping_countries: DEDEData.default.de_DE.models.shipping_countries,
    calories_covered: DEDEData.default.de_DE.shared.calories_covered,
    text_field: DEDEData.default.de_DE.shared.text_field,
    footer: DEDEData.default.de_DE.shared.footer,
    navigation: DEDEData.default.de_DE.shared.navigation,
    unavailable_dates_card:
      DEDEData.default.de_DE.shared.unavailable_dates_card,
    expert: DEDEData.default.de_DE.shared.expert,
    paid_traffic_breeds: DEDEData.default.de_DE.paid_traffic.breed_autocomplete,
    paid_traffic: DEDEData.default.de_DE.paid_traffic,
    one_time_purchase_footer:
      DEDEData.default.de_DE.shared.one_time_purchase_footer,
    order_summary: DEDEData.default.de_DE.shared.order_summary,
    shared: DEDEData.default.de_DE.shared,
    languages: DEDEData.default.de_DE.models.languages,
    localisation_modal: DEDEData.default.de_DE.shared.localisation_modal,
    plan_management: DEDEData.default.de_DE.plan_management,
    onboarding_quiz: DEDEData.default.de_DE.onboarding_quiz,
    product_modal: DEDEData.default.de_DE.shared.product_modal,
    gql_errors: DEDEData.default.de_DE.gql_errors,
    archive_reasons: DEDEData.default.de_DE.archive_reasons,
    care: DEDEData.default.de_DE.care,
    orders: DEDEData.default.de_DE.orders,
    order: DEDEData.default.de_DE.order,
    butternut_app: DEDEData.default.de_DE.butternut_app,
    atoms: DEDEData.default.de_DE.atoms,
    molecules: DEDEData.default.de_DE.molecules,
    organisms: DEDEData.default.de_DE.organisms,
    one_off_box_page: DEDEData.default.de_DE.one_off_box_page, // Note: New atomic OOB page
    swap_and_save: DEDEData.default.de_DE.swap_and_save,
    limited_offer_paused_page: DEDEData.default.de_DE.limited_offer_paused_page,
    treatments_paused_page: DEDEData.default.de_DE.treatments_paused_page,
    ribbon: DEDEData.default.de_DE.ribbon,
    invoices: DEDEData.default.de_DE.invoices,
    ambassadors: DEDEData.default.de_DE.ambassadors,
    ambassadors_shared: DEDEData.default.de_DE.ambassadors_shared,
    ambassadors_funnel: DEDEData.default.de_DE.ambassadors_funnel,
    ambassadors_account: DEDEData.default.de_DE.ambassadors_account,
    modals: DEDEData.default.de_DE.modals,
    blog: DEDEData.default.de_DE.blog,
    routes: DEDEData.default.de_DE.routes,
    localised_address: DEDEData.default.de_DE.localised_address
  },
  'it-IT': {
    dynamic: ITITData.default.it_IT.dynamic,
    surveys: ITITData.default.it_IT.surveys,
    learn_more_about_butternut:
      ITITData.default.it_IT.shared.learn_more_about_butternut,
    frequently_asked_questions:
      ITITData.default.it_IT.shared.frequently_asked_questions,
    reviews: ITITData.default.it_IT.reviews,
    account: ITITData.default.it_IT.account,
    homepage: ITITData.default.it_IT.homepage,
    home_page: ITITData.default.it_IT.home_page,
    contacts: ITITData.default.it_IT.contacts,
    our_products: ITITData.default.it_IT.our_products,
    our_story: ITITData.default.it_IT.our_story,
    making_our_meals: ITITData.default.it_IT.making_our_meals,
    wizard_flow: ITITData.default.it_IT.wizard_flow,
    self_resolution_flow: ITITData.default.it_IT.self_resolution_flow,
    customer_issue_management_flow:
      ITITData.default.it_IT.customer_issue_management_flow,
    plans_flow: ITITData.default.it_IT.plans_flow,
    simplified_plans_flow: ITITData.default.it_IT.simplified_plans_flow,
    checkout: ITITData.default.it_IT.checkout,
    thank_you: ITITData.default.it_IT.thank_you,
    models: ITITData.default.it_IT.models,
    product_categories: ITITData.default.it_IT.models.product_categories,
    post_signup_wizard: ITITData.default.it_IT.post_signup_wizard,
    dashboard: ITITData.default.it_IT.dashboard,
    ds_app_pay_on_signup_flow: ITITData.default.it_IT.ds_app_pay_on_signup_flow,
    shipping_countries: ITITData.default.it_IT.models.shipping_countries,
    calories_covered: ITITData.default.it_IT.shared.calories_covered,
    text_field: ITITData.default.it_IT.shared.text_field,
    footer: ITITData.default.it_IT.shared.footer,
    navigation: ITITData.default.it_IT.shared.navigation,
    unavailable_dates_card:
      ITITData.default.it_IT.shared.unavailable_dates_card,
    expert: ITITData.default.it_IT.shared.expert,
    paid_traffic_breeds: ITITData.default.it_IT.paid_traffic.breed_autocomplete,
    paid_traffic: ITITData.default.it_IT.paid_traffic,
    one_time_purchase_footer:
      ITITData.default.it_IT.shared.one_time_purchase_footer,
    order_summary: ITITData.default.it_IT.shared.order_summary,
    shared: ITITData.default.it_IT.shared,
    languages: ITITData.default.it_IT.models.languages,
    localisation_modal: ITITData.default.it_IT.shared.localisation_modal,
    plan_management: ITITData.default.it_IT.plan_management,
    onboarding_quiz: ITITData.default.it_IT.onboarding_quiz,
    product_modal: ITITData.default.it_IT.shared.product_modal,
    gql_errors: ITITData.default.it_IT.gql_errors,
    archive_reasons: ITITData.default.it_IT.archive_reasons,
    care: ITITData.default.it_IT.care,
    orders: ITITData.default.it_IT.orders,
    order: ITITData.default.it_IT.order,
    butternut_app: ITITData.default.it_IT.butternut_app,
    atoms: ITITData.default.it_IT.atoms,
    molecules: ITITData.default.it_IT.molecules,
    organisms: ITITData.default.it_IT.organisms,
    one_off_box_page: ITITData.default.it_IT.one_off_box_page,
    swap_and_save: ITITData.default.it_IT.swap_and_save,
    limited_offer_paused_page: ITITData.default.it_IT.limited_offer_paused_page,
    treatments_paused_page: ITITData.default.it_IT.treatments_paused_page,
    ribbon: ITITData.default.it_IT.ribbon,
    invoices: ITITData.default.it_IT.invoices,
    ambassadors: ITITData.default.it_IT.ambassadors,
    ambassadors_shared: ITITData.default.it_IT.ambassadors_shared,
    ambassadors_funnel: ITITData.default.it_IT.ambassadors_funnel,
    ambassadors_account: ITITData.default.it_IT.ambassadors_account,
    modals: ITITData.default.it_IT.modals,
    blog: ITITData.default.it_IT.blog,
    routes: ITITData.default.it_IT.routes,
    localised_address: ITITData.default.it_IT.localised_address
  }
}

const namespaceToPath = (ns: string): string => {
  switch (ns) {
    case 'learn_more_about_butternut':
      return 'shared.learn_more_about_butternut'
    case 'frequently_asked_questions':
      return 'shared.frequently_asked_questions'
    case 'reviews':
      return 'reviews'
    case 'account':
      return 'account'
    case 'homepage':
      return 'homepage'
    case 'home_page':
      return 'home_page'
    case 'wizard_flow':
      return 'wizard_flow'
    case 'self_resolution_flow':
      return 'self_resolution_flow'
    case 'customer_issue_management_flow':
      return 'customer_issue_management_flow'
    case 'plans_flow':
      return 'plans_flow'
    case 'simplified_plans_flow':
      return 'simplified_plans_flow'
    case 'checkout':
      return 'checkout'
    case 'ds_app_pay_on_signup_flow':
      return 'ds_app_pay_on_signup_flow'
    case 'thank_you':
      return 'thank_you'
    case 'contacts':
      return 'contacts'
    case 'our_products':
      return 'our_products'
    case 'our_story':
      return 'our_story'
    case 'making_our_meals':
      return 'making_our_meals'
    case 'models':
      return 'models'
    case 'product_categories':
      return 'models.product_categories'
    case 'dashboard':
      return 'dashboard'
    case 'post_signup_wizard':
      return 'post_signup_wizard'
    case 'shipping_countries':
      return 'models.shipping_countries'
    case 'calories_covered':
      return 'shared.calories_covered'
    case 'text_field':
      return 'shared.text_field'
    case 'footer':
      return 'shared.footer'
    case 'navigation':
      return 'shared.navigation'
    case 'unavailable_dates_card':
      return 'shared.unavailable_dates_card'
    case 'expert':
      return 'shared.expert'
    case 'paid_traffic_breeds':
      return 'paid_traffic.breed_autocomplete'
    case 'paid_traffic':
      return 'paid_traffic'
    case 'one_time_purchase_footer':
      return 'shared.one_time_purchase_footer'
    case 'order_summary':
      return 'shared.order_summary'
    case 'shared':
      return 'shared'
    case 'localisation_modal':
      return 'shared.localisation_modal'
    case 'plan_management':
      return 'plan_management'
    case 'onboarding_quiz':
      return 'onboarding_quiz'
    case 'product_modal':
      return 'shared.product_modal'
    case 'gql_errors':
      return 'gql_errors'
    case 'archive_reasons':
      return 'archive_reasons'
    case 'care':
      return 'care'
    case 'order':
      return 'order'
    case 'orders':
      return 'orders'
    case 'butternut_app':
      return 'butternut_app'
    case 'atoms':
      return 'atoms'
    case 'molecules':
      return 'molecules'
    case 'organisms':
      return 'organisms'
    case 'one_off_box_page':
      return 'one_off_box_page'
    case 'swap_and_save':
      return 'swap_and_save'
    case 'limited_offer_paused_page':
      return 'limited_offer_paused_page'
    case 'treatments_paused_page':
      return 'treatments_paused_page'
    case 'invoices':
      return 'invoices'
    case 'ribbon':
      return 'ribbon'
    case 'blog':
      return 'blog'
    case 'ambassadors':
      return 'ambassadors'
    case 'ambassadors_shared':
      return 'ambassadors_shared'
    case 'ambassadors_funnel':
      return 'ambassadors_funnel'
    case 'ambassadors_account':
      return 'ambassadors_account'
    case 'modals':
      return 'modals'
    case 'routes':
      return 'routes'
    case 'localised_address':
      return 'localised_address'
    default: {
      return ''
    }
  }
}
const dateLocale = (locale: Language): Locale => {
  switch (locale) {
    case 'cs_CZ':
      return cs
    case 'en':
      return enGB
    case 'nl':
      return nl
    case 'nl_BE':
      return nlBE
    case 'fr':
      return fr
    case 'de_DE':
      return de
    case 'pl_PL':
    case 'pl':
      return pl
    case 'it_IT':
      return it
    default:
      ensureNever(locale)
      return enGB
  }
}
const development = process.env.NODE_ENV === 'development'
const phraseBackendEnabled = Cookies.get('phraseapp_enabled') === 'true'
Cookies.remove('phraseapp_enabled')

const pseudolocaliseCookie = Cookies.get('pseudolocalise') === 'true'

// i18next expects kebab-case
const convertLanguageToKebabCase = (lng: Language): string => {
  switch (lng) {
    case 'nl_BE':
      return 'nl-BE'
    case 'pl_PL':
      return 'pl-PL'
    case 'de_DE':
      return 'de-DE'
    case 'cs_CZ':
      return 'cs-CZ'
    case 'it_IT':
      return 'it-IT'
    default:
      return lng
  }
}

const fallbackLanguage = (lng: Language): string | Array<string> => {
  switch (lng) {
    case 'pl_PL':
      return ['pl', 'en']
    case 'nl_BE':
      return ['nl', 'en']
    case 'nl':
    case 'fr':
    case 'de_DE':
    case 'pl':
    case 'it_IT':
    default:
      return 'en'
  }
}

const initI18n = (
  lng: Language,
  pseudolocalise = pseudolocaliseCookie,
  phraseEnabled = phraseBackendEnabled
): void => {
  if (i18n.isInitialized) return
  i18n.on('initialized', () => {
    document.dispatchEvent(
      new CustomEvent('translationInitialised', { detail: i18n })
    )
  })

  i18n
    .use(
      new Pseudo({
        enabled: pseudolocalise,
        languageToPseudo: 'en',
        letterMultiplier: 1
      })
    )
    .use(initReactI18next)
    .init({
      ns: [
        'ambassadors',
        'ambassadors_shared',
        'ambassadors_funnel',
        'ambassadors_account',
        'dynamic',
        'surveys',
        'learn_more_about_butternut',
        'frequently_asked_questions',
        'reviews',
        'account',
        'homepage',
        'home_page',
        'contacts',
        'thank_you',
        'our_products',
        'our_story',
        'making_our_meals',
        'wizard_flow',
        'self_resolution_flow',
        'customer_issue_management_flow',
        'plans_flow',
        'simplified_plans_flow',
        'checkout',
        'models',
        'product_categories',
        'dashboard',
        'post_signup_wizard',
        'ds_app_pay_on_signup_flow',
        'shipping_countries',
        'calories_covered',
        'text_field',
        'footer',
        'navigation',
        'unavailable_dates_card',
        'expert',
        'paid_traffic_breeds',
        'paid_traffic',
        'one_time_purchase_footer',
        'order_summary',
        'shared',
        'languages',
        'localisation_modal',
        'plan_management',
        'onboarding_quiz',
        'product_modal',
        'gql_errors',
        'archive_reasons',
        'care',
        'orders',
        'order',
        'butternut_app',
        'atoms',
        'molecules',
        'organisms',
        'one_off_box_page',
        'swap_and_save',
        'limited_offer_paused_page',
        'treatments_paused_page',
        'invoices',
        'ribbon',
        'modals',
        'routes',
        'localised_address'
      ],
      defaultNS: '',
      lng: convertLanguageToKebabCase(lng),
      react: {
        hashTransKey: () => null
      },
      compatibilityJSON: 'v3', // Add compatibility for v3 JSON format
      fallbackLng: fallbackLanguage(lng),
      debug: development,
      resources: resources,
      saveMissing: true,
      supportedLngs: supportedLanguages,
      missingKeyHandler: (
        lngs: readonly string[],
        ns: string,
        key: string
      ): void => {
        if (development) {
          console.error(
            // eslint-disable-next-line i18next/no-literal-string
            `Missing key: ${key} in namespace: ${ns} for language(s): ${lngs
              .map((lng: string): string => lng)
              .join(', ')}`
          )
        }
      },
      interpolation: {
        skipOnVariables: false, // Reference here: https://www.i18next.com/translation-function/nesting#passing-nesting-to-interpolated
        format: (value, format) => {
          // Ensure that the date is localised by updating its locale
          // ---
          switch (format) {
            case 'uppercase':
              return value.toUpperCase()
            case 'capitalise':
              return StringHelper.capitaliseFirstLetter(value)
            case 'ordinal':
              return ordinality(value, lng)
            case 'fullDate':
              return formatDate(value, 'P', { locale: dateLocale(lng) })
            case 'day':
              return formatDate(value, 'eeee', { locale: dateLocale(lng) })
            case 'month':
              return formatDate(value, 'MMMM', { locale: dateLocale(lng) })
            case 'year':
              return formatDate(value, 'yyyy', { locale: dateLocale(lng) })
            case 'shortenedMonth':
              return formatDate(value, 'MMM', { locale: dateLocale(lng) })
            case 'monthAndDate':
              return formatDate(value, 'MMMM do', { locale: dateLocale(lng) })
            case 'dateAndMonth':
              return formatDate(value, 'd MMMM', { locale: dateLocale(lng) })
            case 'dateNumber':
              return formatDate(value, 'd', { locale: dateLocale(lng) })
            case 'dayDateMonth':
              return formatDate(value, 'iiii, dd MMM', {
                locale: dateLocale(lng)
              })
            case 'dayDateLongMonth':
              return formatDate(value, 'iiii, dd MMMM', {
                locale: dateLocale(lng)
              })
            default:
              return value
          }
        },
        escapeValue: false // not needed for react as it escapes by default
      },
      postProcess: ['pseudo'],
      load: 'currentOnly'
    })
  if (phraseEnabled) {
    // the types the we see when phrase in context editor is enabled do not correspond to
    // expected value here
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
    // @ts-ignore
    i18n.t = (key: string, global: { context: string; ns: string }) => {
      let namespace = ''
      if (key.includes(':')) {
        // split up namespace and rest of key
        const fullKey = key.split(':')
        namespace = fullKey[0]
        key = fullKey[1]
      }
      if (global && global.context) {
        key = `${key}_${global.context}`
      }
      // The Phrase in context editor scans the page and identifies exposed keys that
      // look like this:
      // {{__phrase_${key}__}}
      // we attempted this transformation with their postProcessing library, however,
      // the library does not work with varying namespaces. The following solution
      // allows us to interpolate the keys while integrating the appropriate namespace
      // utilised in the I18n t function.
      // eslint-disable-next-line i18next/no-literal-string
      return `{{__phrase_${namespaceToPath(
        namespace || global.ns || ''
      )}.${key}__}}`
    }
  }
}

export default initI18n
export type { Language }
